import { unwrap } from "@sagot/api-client";
import { EMAIL_MAX_LENGTH, SSO_ERROR_QUERY } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Field, Icon, Input } from "@sagot/ui";
import { useState, type SyntheticEvent } from "react";
import { useSearchParams } from "react-router";
import { useErrorMessage } from "../lib/api-errors";
import { useApi } from "../session/session-provider";
import { FormAlert } from "./components/form-alert";

const REASONS = ["failed", "unavailable", "not_a_member", "domain", "expired"] as const;
type Reason = (typeof REASONS)[number];
const reasonOf = (value: string | null): Reason | null => (value === null ? null : ((REASONS as readonly string[]).includes(value) ? (value as Reason) : "failed"));
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface SsoEntryProps {
  /** What the password form already holds; prefills the work e-mail. */
  email: string;
  /** The password sign-in was refused with `sso_enforced`: open the entry straight away (mount it with a `key` that changes with this). */
  enforced: boolean;
}

/**
 * Sign-in page entry for staff single sign-on: asks the work e-mail, lets the API find the
 * workspace's provider for that domain (`POST /v1/auth/sso/discover`) and goes there with a full
 * page redirect. Shows why a previous attempt failed (`?sso_error=`).
 */
export function SsoEntry({ email, enforced }: SsoEntryProps) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const [params] = useSearchParams();
  const failure = reasonOf(params.get(SSO_ERROR_QUERY));
  const [open, setOpen] = useState(enforced || failure !== null);
  const [value, setValue] = useState(email);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const shown = open;
  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setNote(null);
    const address = value.trim();
    if (!EMAIL.test(address) || address.length > EMAIL_MAX_LENGTH) {
      setError(t("auth.common.emailInvalid"));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const found = await unwrap(api.POST("/v1/auth/sso/discover", { body: { email: address } }));
      if (found.sso && found.start_url) {
        window.location.assign(found.start_url);
        return;
      }
      setNote(t("auth.login.ssoNone"));
    } catch (caught) {
      setError(message(caught));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3">
      {failure ? <FormAlert title={t(`auth.login.ssoError.${failure}`)} /> : null}
      {enforced ? <FormAlert title={t("auth.login.ssoEnforced")} /> : null}
      {shown ? (
        <form className="space-y-3 rounded-lg border border-line bg-surface p-4" noValidate onSubmit={(event) => void submit(event)} aria-label={t("auth.login.ssoTitle")} data-testid="sso-entry">
          <h2 className="text-sm font-bold">{t("auth.login.ssoTitle")}</h2>
          <Field label={t("auth.common.workEmail")} error={error ?? undefined}>
            <Input type="email" autoComplete="email" inputMode="email" value={value} onChange={(event) => { setValue(event.target.value); }} />
          </Field>
          {note ? <p role="status" className="text-xs text-fg-muted">{note}</p> : null}
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" loading={busy} className="grow">
              <Icon name="log-in" />
              {t("auth.login.ssoContinue")}
            </Button>
            {enforced ? null : (
              <Button type="button" variant="ghost" onClick={() => { setOpen(false); }}>
                {t("auth.login.ssoBack")}
              </Button>
            )}
          </div>
        </form>
      ) : (
        <div className="text-center">
          <button type="button" className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-ink hover:underline" onClick={() => { setValue(email); setOpen(true); }}>
            <Icon name="key" className="size-3.5" />
            {t("auth.login.sso")}
          </button>
        </div>
      )}
    </div>
  );
}
